import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import styles from "./RowLink.module.css";

// The name cell of a table row, linking to that row's detail.
//
// Every list in the app that has a detail page uses this, so the decision —
// bold, ink at rest, primary on hover — lives in one place instead of being
// re-made per page.
//
// Where a row has no detail to open, or the reader may not see it, render
// the plain name instead: a link that 403s is worse than no link. That is
// why this takes no `disabled` prop — the caller decides by rendering
// something else.
//
// `Column.linkTo` (adminer 0.19.0) took over the simple case — a cell whose
// whole content IS the name. This survives for the composite ones, where the
// name sits above a muted second line: `linkTo` wraps the entire cell, which
// would pull the subtitle inside the anchor, bold it (the row-link class sets
// semibold) and make it a click target. Six cells are like that — rule sets,
// disciplines, and the four competition lists.
//
// The upstream refinement that would retire this: export the row-link
// presentation itself, so a composite cell gets the same tokens without
// re-deriving them here. See docs/adminer-redesign.md §12.

export interface RowLinkProps {
  to: string;
  children: ReactNode;
}

export function RowLink({ to, children }: RowLinkProps) {
  return (
    <Link to={to} className={styles.link}>
      {children}
    </Link>
  );
}
